/**
 * Cycle mensuel des créneaux de modèle (`TemplateSlot.cycleWeeks`).
 *
 * Miroir exact de `weekOfMonth` (`backend/src/services/grid-template.service.ts`) :
 * la semaine 1 est celle dont le **lundi** tombe du 1er au 7 du mois, une
 * semaine à cheval sur deux mois appartient au mois de son lundi, et un 5e
 * lundi (29, 30 ou 31) rejoue la semaine 1. Le cycle compte donc toujours
 * quatre semaines et le mois suivant repart à 1 : rien ne glisse.
 */

export const MONTH_CYCLE_WEEKS = 4
export const MONTH_CYCLE_WEEK_NUMBERS = [1, 2, 3, 4] as const

/** Rang du lundi dans son mois, de 1 à 5. */
function mondayRank(dayOfMonth: number): number {
  return Math.floor((dayOfMonth - 1) / 7) + 1
}

/** Semaine du cycle (1 à 4) d'un lundi donné par son jour du mois. */
export function cycleWeekOfMonday(dayOfMonth: number): number {
  const rank = mondayRank(dayOfMonth)
  return rank > MONTH_CYCLE_WEEKS ? 1 : rank
}

/** Un créneau sans semaine cochée joue toutes les semaines. */
export function isActiveInCycleWeek(cycleWeeks: readonly number[] | null | undefined, week: number): boolean {
  const weeks = cycleWeeks ?? []
  return weeks.length === 0 || weeks.includes(week)
}

/** Créneau hérité de l'ancien cycle à cinq semaines, coché uniquement en 5 :
 *  il ne serait plus jamais posé. */
export function isFifthWeekOnly(cycleWeeks: readonly number[] | null | undefined): boolean {
  const weeks = cycleWeeks ?? []
  return weeks.length > 0 && weeks.every(week => week === 5)
}

export interface CycleMonday {
  /** Lundi au format `AAAA-MM-JJ`. */
  key: string
  /** Libellé court `JJ/MM`. */
  label: string
  week: number
  /** Vrai pour un 5e lundi, qui rejoue la semaine 1. */
  fifthMonday: boolean
}

/**
 * Prochains lundis où le créneau joue, à partir de la semaine qui contient
 * `fromDayKey` (`AAAA-MM-JJ`, jour civil d'antenne). Calcul en jours civils
 * purs (UTC), sans fuseau : seule la date du lundi compte.
 */
export function upcomingCycleMondays(
  cycleWeeks: readonly number[] | null | undefined,
  fromDayKey: string,
  count: number,
): CycleMonday[] {
  const [year, month, day] = fromDayKey.split('-').map(Number)
  const cursor = new Date(Date.UTC(year, month - 1, day))
  // Recule jusqu'au lundi de la semaine en cours (getUTCDay : 0 = dimanche).
  cursor.setUTCDate(cursor.getUTCDate() - ((cursor.getUTCDay() + 6) % 7))
  const result: CycleMonday[] = []
  // Borne de sécurité : un cycle vide ou valide trouve ses lundis en < 1 an.
  for (let guard = 0; guard < 60 && result.length < count; guard++) {
    const dayOfMonth = cursor.getUTCDate()
    const week = cycleWeekOfMonday(dayOfMonth)
    if (isActiveInCycleWeek(cycleWeeks, week)) {
      const dd = String(dayOfMonth).padStart(2, '0')
      const mm = String(cursor.getUTCMonth() + 1).padStart(2, '0')
      result.push({
        key: `${cursor.getUTCFullYear()}-${mm}-${dd}`,
        label: `${dd}/${mm}`,
        week,
        fifthMonday: mondayRank(dayOfMonth) > MONTH_CYCLE_WEEKS,
      })
    }
    cursor.setUTCDate(cursor.getUTCDate() + 7)
  }
  return result
}

/** Prochain 5e lundi, pour expliquer la règle avec une date réelle. */
export function nextFifthMonday(fromDayKey: string): CycleMonday | null {
  return upcomingCycleMondays([], fromDayKey, 60).find(item => item.fifthMonday) ?? null
}
